import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { DateTime, Duration } from 'luxon';
import { Observable, of } from 'rxjs';
import { DateRange } from 'src/app/common/daterange';
import { Totals } from 'src/app/pages/dashboard/widgets/totals';
import { GetAttendanceIssuesOptions, WorkSchedulesAttendanceIssue } from 'src/app/services/schedules';
import { sortByApiParam } from 'src/app/util/sorting';
import { HighestRatioMode, Screencast, TimeuseScore } from 'src/models';
import { ApiService } from '../api.service';
import { PagedResult, PagingService } from '../paging/paging.service';
import { UserService } from '../user/user.service';
import { DemoSeedService } from './demo-seed.service';
import {
    AverageGroup,
    GetStatsOptions,
    ModeRatio,
    ModeRatioExtended,
    NewStatsComcatResult,
    NewStatsCompanyResult,
    NewStatsOptions, PaidLeaveData,
    RawRatio,
    StatsService,
    StatsTotal,
    Summary,
    SummaryRatio,
    WorkLifeBalanceParams,
    WorkLifeBalanceResult,
    WorkRange
} from '../stats/stats.service';
import { MockDataStore, User } from './mock-data-store';
import { getDemoDateBuckets, withBucketDateOptions } from './demo-date-range.mock';
import {
  DEMO_REPORT_PROJECTS,
  DEMO_REPORT_TASKS,
  buildMockProjectsReportStats,
  buildMockTasksTotalStats,
  isProjectsReportGroupBy
} from './demo-projects-report.mock';
import { buildMockScreencastsPage, countMockScreencasts } from './demo-screencasts.mock';
import { buildMockSciComcatList } from './demo-software-cost-insights.mock';
import { buildDemoReportUsers, filterDemoReportUsers, getDemoTeamId, getDemoTeamName } from './demo-report-users.mock';

/** Resolves how many users mock totals should represent from stats API params. */
export function resolveMockUserCount(
  apiParams?: { user?: string | string[] },
  allUsersFallback = 5,
): number {
  const user = apiParams?.user;
  if (user == null || user === '') {
    return allUsersFallback;
  }
  if (Array.isArray(user)) {
    return user.length;
  }
  if (user === 'all-self-on-reports' || user === 'all-on-reports') {
    return allUsersFallback;
  }
  if (user.includes(',')) {
    return user.split(',').filter(id => id.length > 0).length;
  }
  return 1;
}

/** Resolves explicit mock user ids from stats API params, or null for all-users filters. */
export function resolveMockUserIds(
  apiParams?: { user?: string | string[] },
): string[] | null {
  const user = apiParams?.user;
  if (user == null || user === '') {
    return null;
  }
  if (Array.isArray(user)) {
    return user;
  }
  if (user === 'all-self-on-reports' || user === 'all-on-reports') {
    return null;
  }
  if (user.includes(',')) {
    return user.split(',').filter(id => id.length > 0);
  }
  return [user];
}

const MOCK_COMCAT_SCORES: TimeuseScore[] = [
  TimeuseScore.Productive,
  TimeuseScore.Unproductive,
  TimeuseScore.Neutral,
  TimeuseScore.Unrated,
];

function resolveMockComcatScore(
  index: number,
  mode?: HighestRatioMode | 'all' | 'unrated',
): TimeuseScore {
  switch (mode) {
    case 'unproductive':
      return TimeuseScore.Unproductive;
    case 'neutral':
      return TimeuseScore.Neutral;
    case 'unrated':
      return TimeuseScore.Unrated;
    case 'productive':
      return TimeuseScore.Productive;
    default:
      return MOCK_COMCAT_SCORES[index % MOCK_COMCAT_SCORES.length];
  }
}

type MockComcatMode = HighestRatioMode | 'all' | 'unrated';

function parseMockComcatIndex(comCat?: string | string[]): number | null {
  const value = Array.isArray(comCat) ? comCat[0] : comCat;
  if (!value) {
    return null;
  }
  const match = /^category-(\d+)$/.exec(value);
  if (!match) {
    return null;
  }
  const index = Number(match[1]);
  return Number.isInteger(index) ? index : null;
}

function applyScorePrefilter<T extends { score?: unknown }>(
  rows: T[],
  prefilter?: NewStatsOptions['prefilter[score]'],
): T[] {
  if (prefilter == null || prefilter === '' || (Array.isArray(prefilter) && prefilter.length === 0)) {
    return rows;
  }
  const allowed = new Set((Array.isArray(prefilter) ? prefilter : [prefilter]).map(value => Number(value)));
  return rows.filter((row) => {
    const score = row?.score;
    if (typeof score === 'number' || typeof score === 'string') {
      return allowed.has(Number(score));
    }
    return true;
  });
}

@Injectable({
  providedIn: 'root',
})
export class MockStatsService extends StatsService implements OnDestroy {
  constructor(
    api: ApiService,
    paging: PagingService,
    users: UserService,
    store: Store,
    private readonly demoSeed: DemoSeedService,
  ) {
    super(api, paging, users, store);
  }

  private seedFloat(...scope: (string | number)[]): number {
    return this.demoSeed.float('mock-stats', ...scope);
  }

  private seedBetween(min: number, max: number, ...scope: (string | number)[]): number {
    return this.demoSeed.between(min, max, 'mock-stats', ...scope);
  }

  private seedInt(min: number, max: number, ...scope: (string | number)[]): number {
    return this.demoSeed.int(min, max, 'mock-stats', ...scope);
  }

  // Use MockDataStore singleton to get the updated user list
  private mockDataStore = MockDataStore.getInstance();

  // Get users from MockDataStore instead of hardcoding
  public get mockUsers(): User[] {
    const users = this.mockDataStore.getAllUsers();
    return users;
  }

  public readonly standardRatios = {
    productive: 0.80,    // 80% productive time
    unproductive: 0.12,  // 12% unproductive time
    neutral: 0.3,        // 30% neutral time (note: this adds up to more than 100%)
    meeting: 0.20,       // 20% meeting time
    computer: 0.65,      // 65% computer time
    mobile: 0.10,        // 10% mobile time
    manual: 0.15,        // 15% manual time
    offcomputer: 0.10,   // 10% off-computer time
    paidBreak: 0.08,     // 8% paid break time
    idle: 0.05,          // 5% idle time
  };

  // Renamed property to store the total values for each user
  private readonly highRatioModeTotal: Record<string, number> = {};

  // Add a property to store unproductive ratio for each user
  private readonly userUnproductiveRatio: Record<string, number> = {};

  // In MockStatsService class:

  // Base values for time calculations (in seconds)
  public baseSeconds: number;
  public userBaseSeconds: string[] = [];

    // Add object pooling to prevent memory leaks
  private readonly objectPool = new Map<string, any[]>();
  private readonly poolSize = 10; // Keep 10 objects in pool

  // Add timeout ID for memory cleanup to prevent memory leaks
  private memoryCleanupTimeoutId: NodeJS.Timeout | null = null;

  // Initialize baseSeconds - average work time per day (2-8 hours in seconds)
  public initBaseSeconds(): void {
    this.baseSeconds = this.seedInt(7200, 28800, 'baseSeconds');

    // Start periodic memory cleanup
    this.performMemoryCleanup();
  }

  // Initialize userBaseSeconds with realistic variations
  public initUserBaseSeconds(userCount?: number): void {
    this.userBaseSeconds = [];
    const actualUserCount = userCount || this.mockUsers.length;
    for (let i = 0; i < actualUserCount; i++) {
      // Create variation around the base seconds (±20%)
      const variationFactor = this.seedBetween(0.8, 1.2, 'userBaseSeconds', i);
      this.userBaseSeconds.push(Math.floor(this.baseSeconds * variationFactor).toString());
    }
  }

  // Helper method to generate mock data arrays more efficiently
  private createMockDataArray<T>(length: number, factory: (index: number) => T): T[] {
    const result: T[] = [];
    for (let i = 0; i < length; i++) {
      result.push(factory(i));
    }
    return result;
  }

  getSummaryRatio(options?: GetStatsOptions): PagedResult<SummaryRatio> {
    const limit = options?.limit < this.mockUsers.length ? options?.limit : this.mockUsers.length;
    const result = this.getEmpty<SummaryRatio>(options) ?? this.paging.getPagedResult<SummaryRatio>(page => {
      const mockData = this.createMockDataArray(limit, i => this.createMockSummaryRatio(i));

      return of({
        data: mockData,
        paging: {
          totalCount: this.mockUsers.length, // Simulate a larger dataset
          cur: page,
          limit,
        },
      });
    }, limit, this.mockUsers.length);

    return result;
  }

  getSummaryRatioLimitUsers(options?: GetStatsOptions): PagedResult<SummaryRatio> {
    return this.createPagedResult<SummaryRatio>(
      this.createMockDataArray(this.mockUsers.length, i => this.createMockSummaryRatio(i + 10)),
    );
  }

  getSummary(options?: NewStatsOptions): PagedResult<Summary> {
    return this.createPagedResult<Summary>(
      this.createMockDataArray(10, i => this.createMockSummary(i)),
    );
  }

  private parseActiveBiasRatioFilter(range: string | undefined, value: number): boolean {
    if (!range) {
      return true;
    }
    const rangeValues = range.split('_');
    const minValue = parseFloat(rangeValues[0]) || 0;
    const maxValue = parseFloat(rangeValues[1]) || 0.99;
    return value >= minValue && value <= maxValue;
  }

  getTotal(options?: NewStatsOptions): PagedResult<Summary> {
    const activeBiasRatioFilter = options?.['filter[activeBiasRatio]'];
    const users = this.resolveGetTotalUsers(options);
    const limit = options?.limit != null && options.limit < users.length ? options.limit : users.length;

    const result = this.getEmpty<Summary>(options) ?? this.paging.getPagedResult<Summary>(page => {
      let mockData = users.slice(0, limit).map((user, index) =>
        this.createMockSummary(index, options, user.id));
      if (activeBiasRatioFilter) {
        mockData = mockData.filter(item =>
          this.parseActiveBiasRatioFilter(activeBiasRatioFilter, item.activeBiasRatio));
      }

      return of({
        data: mockData,
        paging: {
          totalCount: users.length,
          cur: page,
          limit,
        },
      });
    }, limit, users.length);

    return result;
  }

  /** Resolves which demo users report stats should return. Empty user/tag filters stay empty. */
  private resolveGetTotalUsers(options?: NewStatsOptions): User[] {
    const explicitIds = resolveMockUserIds(options);
    if (explicitIds) {
      return explicitIds
        .map(id => this.mockUsers.find(user => user.id === id))
        .filter((user): user is User => !!user);
    }

    return filterDemoReportUsers(buildDemoReportUsers(), {
      user: options?.user,
      tag: options?.tag,
      'include-archived-users': options?.['include-archived-users'],
    })
      .map(reportUser => this.mockUsers.find(user => user.id === reportUser.id))
      .filter((user): user is User => !!user);
  }

  private mockUserIndex(user: User): number {
    return Math.max(this.mockUsers.findIndex(mockUser => mockUser.id === user.id), 0);
  }

  getPaidLeaveData(options?: NewStatsOptions): Observable<PaidLeaveData> {
    return of({
      totalPendingCount: 5,
      totalApprovedCount: 12,
    });
  }

  getRawRatio(options?: GetStatsOptions): PagedResult<RawRatio> {
    return this.createPagedResult<RawRatio>(
      this.createMockDataArray(10, i => this.createMockRawRatio(i)),
    );
  }

  getModeRatio(options?: GetStatsOptions, mode?: 'most' | 'least'): PagedResult<ModeRatio> {
    const limit = options?.limit < this.mockUsers.length ? options?.limit : this.mockUsers.length;
    const result = this.getEmpty<ModeRatioExtended>(options) ?? this.paging.getPagedResult<ModeRatio>(page => {
      const mockData = this.createMockDataArray(limit, i => this.createMockModeRatio(i, mode, options));

      return of({
        data: mockData,
        paging: {
          totalCount: 3, // Simulate a larger dataset
          cur: page,
          limit,
        },
      });
    }, limit, 100);

    return result;
  }

  getAverageWorkedGroups(options?: GetStatsOptions): PagedResult<AverageGroup> {
    return this.createPagedResult<AverageGroup>(
      this.createMockDataArray(5, i => this.createMockAverageGroup(i)),
    );
  }

  getAttendanceIssues(options?: GetAttendanceIssuesOptions): PagedResult<WorkSchedulesAttendanceIssue> {
    return this.createPagedResult<WorkSchedulesAttendanceIssue>(
      this.createMockDataArray(5, i => this.createMockAttendanceIssue(i)),
    );
  }

  getTimesheetModeRatioDaily(options?: GetStatsOptions): PagedResult<ModeRatio[]> {
    return this.createPagedResult<ModeRatio[]>(
      this.createMockDataArray(7, () => this.createMockDataArray(3, i => this.createMockModeRatio(i))),
    );
  }

  getTimesheetRawRatioDaily(options?: GetStatsOptions): PagedResult<RawRatio[]> {
    return this.createPagedResult<RawRatio[]>(
      this.createMockDataArray(7, () => this.createMockDataArray(3, i => this.createMockRawRatio(i))),
    );
  }

  getTimesheetSummaryRatioDaily(options?: GetStatsOptions): PagedResult<SummaryRatio[]> {
    return this.createPagedResult<SummaryRatio[]>(
      this.createMockDataArray(7, () => this.createMockDataArray(3, i => this.createMockSummaryRatio(i))),
    );
  }

  getTimesheetWorkRangeDaily(options: GetStatsOptions): PagedResult<WorkRange[]> {
    return this.createPagedResult<WorkRange[]>(
      this.createMockDataArray(7, () => this.createMockDataArray(3, i => this.createMockWorkRange(i))),
    );
  }

  getProjectsTotal(options?: GetStatsOptions): PagedResult<StatsTotal> {
    const rows = buildMockProjectsReportStats(
      { int: (min, max, ...scope) => this.seedInt(min, max, ...scope) },
      {
        ...(options as NewStatsOptions),
        'group-by': 'projectId',
      },
    );
    const mockData = rows.map(row => ({
      id: row.projectId,
      name: row.projectName,
      total: row.total,
    } as StatsTotal));
    const limit = options?.limit ?? mockData.length;
    return this.createPagedResult<StatsTotal>(mockData.slice(0, limit));
  }

  getTasksTotal(options?: GetStatsOptions): PagedResult<StatsTotal> {
    const seed = { int: (min, max, ...scope) => this.seedInt(min, max, ...scope) };
    const base = buildMockTasksTotalStats(seed, options ?? {});
    const fields = String(options?.fields || '');
    const wantsUserId = fields.includes('userId');
    const explicitIds = wantsUserId ? resolveMockUserIds(options) : null;
    const demoUsers = wantsUserId
      ? filterDemoReportUsers(buildDemoReportUsers(), {
        user: options?.user,
        tag: (options as NewStatsOptions)?.tag,
      })
      : [];
    const expansionIds = explicitIds ?? demoUsers.map(user => user.id);

    const mockData = wantsUserId && expansionIds.length > 0
      ? expansionIds.flatMap(userId => base.map(task => ({
        ...task,
        userId,
        total: Math.max(
          60,
          Math.round(
            (task.total / Math.max(1, expansionIds.length))
            * (this.seedInt(85, 115, 'tasks-total-user', userId, task.id || '') / 100),
          ),
        ),
      } as StatsTotal)))
      : base;

    const limit = options?.limit ?? mockData.length;
    return this.createPagedResult<StatsTotal>(mockData.slice(0, limit));
  }

  getTimesheetTasks(options?: GetStatsOptions): PagedResult<StatsTotal> {
    const rows = buildMockProjectsReportStats(
      { int: (min, max, ...scope) => this.seedInt(min, max, ...scope) },
      {
        ...(options as NewStatsOptions),
        'group-by': 'taskId,userId,date',
        period: options?.period || 'days',
        interval: options?.interval ?? 1,
      },
    );
    const mockData = rows.map(row => {
      const task = DEMO_REPORT_TASKS.find(item => item.id === row.taskId);
      const project = DEMO_REPORT_PROJECTS.find(item => item.id === task?.projectId);
      return {
        id: row.taskId,
        name: row.taskName,
        total: row.total,
        userId: row.userId,
        project: task?.projectId,
        projectName: project?.name,
        date: row.date ? DateTime.fromISO(row.date).toISODate() : undefined,
      } as StatsTotal;
    });
    const limit = options?.limit ?? mockData.length;
    return this.createPagedResult<StatsTotal>(mockData.slice(0, limit));
  }

  getWorkRange(options?: GetStatsOptions): PagedResult<WorkRange> {
    const limit = options?.limit < this.mockUsers.length ? options?.limit : this.mockUsers.length;
    const result = this.getEmpty<WorkRange>(options) ?? this.paging.getPagedResult<WorkRange>(page => {
      const mockData = this.createMockDataArray(limit, i => this.createMockWorkRange(i));

      return of({
        data: mockData,
        paging: {
          totalCount: 10, // Simulate a larger dataset
          cur: page,
          limit,
        },
      });
    }, limit, this.mockUsers.length);

    return result;
  }

  public getScreencasts(options?: GetStatsOptions): PagedResult<Screencast> {
    const limit = options?.limit ?? 50;
    const result = this.getEmpty<Screencast>(options) ?? this.paging.getPagedResult<Screencast>(page => {
      const mockData = buildMockScreencastsPage(
        { int: (min, max, ...scope) => this.seedInt(min, max, ...scope) },
        options,
        page,
        limit,
      );

      return of({
        data: mockData,
        paging: {
          totalCount: countMockScreencasts(options),
          cur: page,
          limit,
        },
      });
    }, limit, countMockScreencasts(options));

    return result;
  }

  newStats<T>(options: NewStatsOptions, mode?: HighestRatioMode): PagedResult<T> {
    const limit = options.limit ?? 10;
    const users = this.resolveGetTotalUsers(options);
    const comCatIndex = parseMockComcatIndex(options['prefilter[comCat]']);

    let mockData: any[];

    if (options['group-by'] === 'comCat') {
      mockData = users.length ? buildMockSciComcatList() : [];
    } else if (options['group-by'] === 'comCat,score' || options['group-by'] === 'comCat,userId') {
      // Fixed catalog (all mock users × scores) so presets and sidepanels share the same apps.
      mockData = users.length
        ? Array.from({ length: this.getMockComcatCatalogSize() }, (_, i) =>
          this.createMockComcatResult(i, mode, options))
          .filter(row => row.total > 0)
        : [];
    } else if (options['group-by'] === 'company') {
      mockData = users.length ? [this.createMockCompanyResult(0, mode, options)] : [];
    } else if (comCatIndex != null) {
      mockData = users
        .map(user => ({
          ...this.createMockUserResult(this.mockUserIndex(user), mode, options),
          total: this.getUserComcatTime(user, comCatIndex, options, mode),
        }))
        .filter(row => row.total > 0);
    } else {
      mockData = users.map(user => this.createMockUserResult(this.mockUserIndex(user), mode, options));
    }

    mockData = applyScorePrefilter(mockData, options['prefilter[score]']);
    mockData = sortByApiParam(mockData, options.sort);

    return this.paging?.getPagedResult<T>(offset => {
      return of({
        data: mockData.slice(offset, offset + limit),
        paging: {
          totalCount: mockData.length,
          cur: offset,
          limit,
        },
      });
    }, limit, mockData.length) || this.createPagedResult<T>(mockData as any);
  }

  newStatsTotal<T>(options: NewStatsOptions): PagedResult<T> {
    const limit = options.limit ?? 100;
    const datePaging = options['date-paging'] === 1 || options['group-by'] === 'date';

    if (datePaging) {
      const dr = DateRange.fromOptions(options);
      const divided = dr.setTimezone(options.timezone || dr.timezone)
        .divide(
          Duration.fromObject({ [options.period || 'days']: options.interval ?? 1 }),
          options.period || true,
        );
      const pageLimit = options['date-limit'] || limit;
      const allRows = divided.map((dayRange) => {
        const bucketOptions = withBucketDateOptions(options, dayRange);
        const totals = this.getDemoTotals(bucketOptions);
        return {
          ...this.createMockModeRatioFromTotals(totals),
          date: dayRange.start.toUTC().toISO(),
        };
      });
      const sortedRows = sortByApiParam(allRows, options.sort);

      // PagingService calls this with an item offset (0, 5, 10, …), not a page index.
      return this.paging.getPagedResult<T>(offset => {
        return of({
          data: sortedRows.slice(offset, offset + pageLimit) as T[],
          paging: { totalCount: sortedRows.length, cur: offset, limit: pageLimit },
        });
      }, pageLimit, sortedRows.length);
    }

    if (options['group-by'] === 'comCat,score' || options['group-by'] === 'comCat,userId') {
      const comcatMockData = Array(this.mockUsers.length * 4).fill(0).map((_, i) => ({
        ...this.createMockComcatResult(i),
        total: this.seedInt(0, 39999, 'newStatsTotal', 'comcat', i),
      }));
      return this.paging.getPagedResult<T>(page => of({
        data: comcatMockData as T[],
        paging: { totalCount: comcatMockData.length, cur: page, limit },
      }), limit);
    }

    if (options['group-by'] === 'company') {
      const totals = this.getDemoTotals(options);
      const companyMockData = [this.createMockModeRatioFromTotals(totals)];
      return this.paging.getPagedResult<T>(page => of({
        data: companyMockData as T[],
        paging: { totalCount: companyMockData.length, cur: page, limit },
      }), limit);
    }

    if (isProjectsReportGroupBy(String(options['group-by']), options)) {
      const projectsMockData = buildMockProjectsReportStats(
        { int: (min, max, ...scope) => this.seedInt(min, max, ...scope) },
        options,
      );

      return this.paging.getPagedResult<T>(offset => {
        return of({
          data: projectsMockData.slice(offset, offset + limit) as T[],
          paging: { totalCount: projectsMockData.length, cur: offset, limit },
        });
      }, limit, projectsMockData.length);
    }

    const users = this.resolveGetTotalUsers(options);
    const mockData = sortByApiParam(
      users.map((user) => {
        const userIndex = Math.max(0, this.mockUsers.findIndex(u => u.id === user.id));
        return this.createMockHoursTrackedUserResult(userIndex, options);
      }),
      options.sort,
    );

    return this.paging.getPagedResult<T>(offset => {
      return of({
        data: mockData.slice(offset, offset + limit) as T[],
        paging: { totalCount: mockData.length, cur: offset, limit },
      });
    }, limit, mockData.length);
  }

  newTimesheetTotal<T>(options: NewStatsOptions): PagedResult<T> {
    const limit = options['date-limit'] || options.limit || 100;
    const dr = DateRange.fromOptions(options);
    const divided = dr.setTimezone(options.timezone || dr.timezone)
      .divide(
        Duration.fromObject({ [options.period || 'days']: options.interval ?? 1 }),
        options.period || true,
      );
    const groupBy = String(options['group-by'] || '');

    return this.paging.getPagedResult<T>(page => {
      const dayRange = divided[page];
      if (!dayRange) {
        return of({
          data: [],
          paging: { totalCount: divided.length, cur: page, limit: 1 },
        });
      }

      const date = dayRange.start.toUTC().toISO();
      const bucketOptions = withBucketDateOptions(options, dayRange);
      let data: unknown[];

      if (groupBy === 'company') {
        data = [{ ...this.createMockModeRatio(page, undefined, bucketOptions), date }];
      } else if (groupBy.includes('userId')) {
        data = [Array(this.mockUsers.length).fill(0).map((_, i) => ({
          ...this.createMockHoursTrackedUserResult(i, bucketOptions),
          date,
        }))];
      } else {
        data = [{ ...this.createMockModeRatio(page, undefined, bucketOptions), date }];
      }

      return of({
        data: data as T[],
        paging: { totalCount: divided.length, cur: page, limit: 1 },
      });
    }, limit, divided.length);
  }

  newStatsByUsers<T>(options: NewStatsOptions): PagedResult<T> {
    const users = this.resolveGetTotalUsers(options);
    let mockData: any[];

    if (options['group-by'] === 'comCat,score' || options['group-by'] === 'comCat,userId') {
      mockData = users.length
        ? Array(users.length).fill(0).map((_, i) => this.createMockComcatResult(i))
        : [];
    } else if (options['group-by'] === 'company') {
      mockData = users.length ? [this.createMockCompanyResult(0, undefined, options)] : [];
    } else {
      mockData = users.map(user => {
        const i = this.mockUserIndex(user);
        return {
          ...this.createMockUserResult(i, undefined, options),
          lastActive: DateTime.now().minus({ hours: this.seedInt(0, 23, 'newStatsByUsers', 'lastActive', i) }).toISO(),
          status: ['active', 'away', 'offline'][this.seedInt(0, 2, 'newStatsByUsers', 'status', i)],
        };
      });
    }

    return this.createPagedResult<any>(sortByApiParam(mockData, options.sort));
  }

  newStatsTimesheet<T>(options: NewStatsOptions): PagedResult<T> {
    const buckets = getDemoDateBuckets(options);
    const limit = options.limit || 100;
    const groupBy = String(options['group-by'] || 'company');
    const users = this.resolveGetTotalUsers(options);

    return this.paging.getPagedResult<T>(offset => {
      const slice = buckets.slice(offset, offset + limit);
      const data = slice.map(bucket => {
        const bucketOptions = withBucketDateOptions(options, bucket);
        if (groupBy === 'userId') {
          return users.map(user => this.createMockUserResult(this.mockUserIndex(user), undefined, bucketOptions));
        }
        return users.length ? [this.createMockCompanyResult(0, undefined, bucketOptions)] : [];
      });

      return of({
        data: data as T[],
        paging: { totalCount: buckets.length, cur: offset, limit },
      });
    }, limit, buckets.length);
  }

  workLifeBalance(options: WorkLifeBalanceParams = {}, allowWlConfigParams = true): PagedResult<WorkLifeBalanceResult> {
    const limit = options.limit || 10;
    const page = options.page || 1;

    const isGroupByTag =
      options.resolve === 'currentTagId' ||
      (typeof options['group-by'] === 'string' && options['group-by'].includes('currentTagId'));

    const mockData: WorkLifeBalanceResult[] = isGroupByTag
      ? this.createMockWorkLifeBalanceGroupRows(limit)
      : Array(this.mockUsers.length).fill(0).map((_, i) =>
        this.createMockWorkLifeBalanceResult(i + ((page - 1) * limit)),
      );

    return this.paging?.getPagedResult<WorkLifeBalanceResult>(pageNum => {
      return of({
        data: mockData,
        paging: {
          totalCount: mockData.length,
          cur: pageNum,
          limit,
        },
      });
    }, limit) || this.createPagedResult<WorkLifeBalanceResult>(mockData);
  }

  /**
   * Executive / team group widgets: one row per department (tag), varied averages, null separator
   * so work-life widget most/least slices match the real API.
   */
  private createMockWorkLifeBalanceGroupRows(limit: number): WorkLifeBalanceResult[] {
    const byTag = new Map<string, { tagId: string, tagName: string }>();
    for (const u of this.mockUsers) {
      if (!byTag.has(u.tagId)) {
        byTag.set(u.tagId, { tagId: u.tagId, tagName: u.tagName });
      }
    }
    const tags = Array.from(byTag.values());
    const rows: WorkLifeBalanceResult[] = tags.map((tag, i) => {
      const seed = tag.tagId.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
      const avg = 2.2 + (seed % 13) * 0.85 + i * 0.4;
      const roundedAvg = Math.round(avg * 10) / 10;
      const totalIssues = Math.max(1, Math.round(roundedAvg * 1.15 + (seed % 4)));
      const dailyIssues = Math.min(totalIssues, Math.max(1, Math.floor(totalIssues / 3)));
      return {
        userId: tag.tagId,
        userName: tag.tagName,
        totalHours: 28 + (seed % 18),
        lastHour: 17 + (seed % 4),
        day: [8, 9, 8, 9, 7, 0, 0],
        tooManyHours: seed % 4,
        lateHours: Math.floor(seed / 2) % 5,
        outOfShiftWork: seed % 3,
        weekendWork: seed % 2,
        dailyIssues,
        totalIssues,
        total: Math.round(roundedAvg * 8 + totalIssues),
        currentTagId: tag.tagId,
        currentTagName: tag.tagName,
        average: roundedAvg,
        size: Math.max(3, this.mockUsers.filter(u => u.tagId === tag.tagId).length || 4),
      };
    });

    const sorted = [...rows].sort((a, b) => b.average - a.average);
    const mid = Math.max(1, Math.ceil(sorted.length / 2));
    const top = sorted.slice(0, mid);
    const bottom = sorted.slice(mid);
    const withSeparator: (WorkLifeBalanceResult | null)[] = [...top, null, ...bottom];

    return withSeparator.slice(0, limit) as unknown as WorkLifeBalanceResult[];
  }

  getUnratedAppCount(): Observable<number> {
    return of(15);
  }

  // Helper methods to create mock data
  private createPagedResult<T>(data: T[]): PagedResult<T> {
    return this.paging.from(data, Math.max(data.length, 1));
  }

  private createMockSummaryRatio(index: number): SummaryRatio {
    const user = this.mockUsers[index % this.mockUsers.length];
    const s = (field: string) => ['summary-ratio', index, user.id, field] as (string | number)[];
    return {
      userId: user.id,
      total: 28800 + this.seedInt(0, 3599, ...s('total')),
      idleMinsRatio: this.seedFloat(...s('idleMinsRatio')) * 0.2,
      idleSecRatio: this.seedFloat(...s('idleSecRatio')) * 0.1,
      prod: this.seedInt(0, 19999, ...s('prod')),
      prodRatio: 0.7 + this.seedFloat(...s('prodRatio')) * 0.2,
      unprod: this.seedInt(0, 4999, ...s('unprod')),
      unprodRatio: this.seedFloat(...s('unprodRatio')) * 0.2,
      manual: this.seedInt(0, 2999, ...s('manual')),
      manualRatio: this.seedFloat(...s('manualRatio')) * 0.1,
      mobile: this.seedInt(0, 1999, ...s('mobile')),
      mobileRatio: this.seedFloat(...s('mobileRatio')) * 0.05,
      keysPerNonIdleMin: this.seedInt(0, 99, ...s('keysPerNonIdleMin')),
      idleMins: this.seedInt(0, 59, ...s('idleMins')),
      idleSec: this.seedInt(0, 3599, ...s('idleSec')),
      count: 1,
      paidBreak: this.seedInt(0, 1799, ...s('paidBreak')),
      paidBreakRatio: this.seedFloat(...s('paidBreakRatio')) * 0.05,
      modeTotal: 28800,
      scoreTotal: 28800,
      activityLevel: 0.8 + this.seedFloat(...s('activityLevel')) * 0.2,
      unrated: this.seedInt(0, 999, ...s('unrated')),
      paidLeave: 0,
      meeting: this.seedInt(0, 3599, ...s('meeting')),
      meetingRatio: this.seedFloat(...s('meetingRatio')) * 0.1,
    };
  }

  private createMockSummary(index: number, options?: NewStatsOptions, userId?: string): Summary {
    const user = (userId
      ? this.mockUsers.find(mockUser => mockUser.id === userId)
      : undefined) ?? this.mockUsers[index % this.mockUsers.length];
    // Include the selected range so Activity Summary % columns vary across date ranges
    // (absolute totals already scale by workdays; ratios were previously user-stable only).
    const rangeFrom = options?.from
      ? DateTime.fromISO(options.from, { zone: 'utc' }).toISODate()
      : '';
    const rangeTo = options?.to
      ? DateTime.fromISO(options.to, { zone: 'utc' }).toISODate()
      : '';
    const s = (field: string) =>
      ['summary', index, user.id, rangeFrom, rangeTo, field] as (string | number)[];

    const workdays = options?.from && options?.to ? this.calculateWorkdays(options) : 1;
    const baseSeconds = this.getUserBaseSecondsForTotals(user.id) ?? 28800;
    const modeTotal = baseSeconds * workdays;
    const dailyJitter = this.seedInt(0, 3599, ...s('dailyJitter'));
    const total = modeTotal + dailyJitter * workdays;

    const activeBiasRatio = this.seedFloat(...s('activeBiasRatio')) * 0.65;
    const idleMinsRatio = this.seedFloat(...s('idleMinsRatio')) * 0.2;
    const idleSecRatio = this.seedFloat(...s('idleSecRatio')) * 0.1;
    const ratio = 0.8 + this.seedFloat(...s('ratio')) * 0.2;
    const manualRatio = this.seedFloat(...s('manualRatio')) * 0.1;
    const mobileRatio = this.seedFloat(...s('mobileRatio')) * 0.05;
    const unprodRatio = this.seedFloat(...s('unprodRatio')) * 0.2;
    const paidBreakRatio = this.seedFloat(...s('paidBreakRatio')) * 0.05;
    const meetingRatio = this.seedFloat(...s('meetingRatio')) * 0.1;

    const rangeStart = options?.from
      ? DateTime.fromISO(options.from, { zone: 'utc' }).startOf('day')
      : DateTime.now().minus({ days: index }).startOf('day');

    return {
      userId: user.id,
      userName: user.name,
      userArchived: user.archived,
      total,
      idleMinsRatio,
      idleSecRatio,
      ratio,
      ratioTotal: modeTotal,
      manual: Math.floor(modeTotal * manualRatio),
      manualRatio,
      mobile: Math.floor(modeTotal * mobileRatio),
      mobileRatio,
      idleMins: Math.floor((modeTotal * idleMinsRatio) / 60),
      idleSec: Math.floor(modeTotal * idleSecRatio),
      modeTotal,
      activeBiasRatio,
      unprod: Math.floor(modeTotal * unprodRatio),
      unprodRatio,
      paidBreak: Math.floor(modeTotal * paidBreakRatio),
      paidBreakRatio,
      unpaidBreak: Math.floor(modeTotal * this.seedFloat(...s('unpaidBreakRatio')) * 0.03),
      paidLeave: 0,
      meeting: Math.floor(modeTotal * meetingRatio),
      meetingRatio,
      date: rangeStart.toISODate(),
    };
  }

  private createMockRawRatio(index: number): RawRatio {
    const user = this.mockUsers[index % this.mockUsers.length];
    const s = (field: string) => ['raw-ratio', index, user.id, field] as (string | number)[];
    return {
      clicks: this.seedInt(0, 999, ...s('clicks')),
      keys: this.seedInt(0, 4999, ...s('keys')),
      moves: this.seedInt(0, 9999, ...s('moves')),
      idleSec: this.seedInt(0, 3599, ...s('idleSec')),
      idleMins: this.seedInt(0, 59, ...s('idleMins')),
      totalSec: 28800,
      totalMins: 480,
      userId: user.id,
    };
  }

  private createMockModeRatioFromTotals(totals: Totals): ModeRatioExtended {
    const total = totals.total ?? 0;
    const ratios = this.standardRatios;
    const computer = Math.floor(total * ratios.computer);
    const manual = Math.floor(total * ratios.manual);
    const mobile = Math.floor(total * ratios.mobile);
    const paidBreak = Math.floor(total * ratios.paidBreak);
    const meeting = Math.floor(total * ratios.meeting);
    const trackingTotal = computer + manual + mobile;
    const offcomputer = Math.max(0, total - trackingTotal);
    const finalTotal = trackingTotal + offcomputer;

    return {
      computer,
      manual,
      mobile,
      offcomputer,
      partial: 0,
      ratio: finalTotal > 0 ? computer / finalTotal : 0,
      total: finalTotal,
      userId: '',
      count: totals.count,
      paidBreak,
      meeting,
      meetingRatio: finalTotal > 0 ? meeting / finalTotal : 0,
      computerRatio: ratios.computer,
      manualRatio: ratios.manual,
      mobileRatio: ratios.mobile,
      offcomputerRatio: finalTotal > 0 ? offcomputer / finalTotal : 0,
      paidBreakRatio: ratios.paidBreak,
    };
  }

  private createMockModeRatio(index: number, mode?: 'most' | 'least', options?: GetStatsOptions): ModeRatioExtended {
    const userIndex = index % this.mockUsers.length;
    const user = this.mockUsers[userIndex];

    // Use userBaseSeconds instead of hardcoded workHoursPerDay
    // If userBaseSeconds is not initialized, use a default value
    const baseSeconds = this.userBaseSeconds[userIndex]
      ? parseInt(this.userBaseSeconds[userIndex])
      : 28800;

    // Calculate workdays if options are provided
    const workdays = options?.from && options?.to
      ? this.calculateWorkdays(options)
      : 1;

    // Calculate total seconds based on workdays
    const totalSeconds = baseSeconds * workdays;

    // Apply a multiplier based on mode
    const modeMultiplier = mode === 'least' ?
      0.3 + this.seedFloat('mode-ratio', index, user.id, 'least') * 0.05 :
      1;

    const adjustedTotal = Math.floor(totalSeconds * modeMultiplier);

    const variationFactor = 0.9 + this.seedFloat('mode-ratio', index, user.id, 'variation') * 0.2;

    // Calculate ratios with variations
    const computerRatio = this.standardRatios.computer * variationFactor;
    const manualRatio = this.standardRatios.manual * variationFactor;
    const mobileRatio = this.standardRatios.mobile * variationFactor;
    const paidBreakRatio = this.standardRatios.paidBreak * variationFactor;
    const meetingRatio = this.standardRatios.meeting * variationFactor;

    // Calculate actual time values
    const computer = Math.floor(adjustedTotal * computerRatio);
    const manual = Math.floor(adjustedTotal * manualRatio);
    const mobile = Math.floor(adjustedTotal * mobileRatio);
    const paidBreak = Math.floor(adjustedTotal * paidBreakRatio);
    const meeting = Math.floor(adjustedTotal * meetingRatio);

    // Calculate offcomputer time as the remainder
    const trackingTotal = computer + manual + mobile;
    const offcomputer = adjustedTotal - trackingTotal;

    // Ensure offcomputer is not negative
    const finalOffcomputer = Math.max(0, offcomputer);
    const total = trackingTotal + finalOffcomputer;

    return {
      computer,
      manual,
      mobile,
      offcomputer: finalOffcomputer,
      partial: 0,
      ratio: computerRatio,
      total,
      userId: user?.id,
      count: 1,
      paidBreak,
      meeting,
      meetingRatio,
      computerRatio,
      manualRatio,
      mobileRatio,
      offcomputerRatio: finalOffcomputer / total,
      paidBreakRatio,
    };
  }

  private createMockWorkRange(index: number): WorkRange {
    const user = this.mockUsers[index % this.mockUsers.length];
    const date = DateTime.now().minus({ days: index }).toISODate();
    return {
      userId: user.id,
      start: `${date}T09:00:00.000Z`,
      end: `${date}T17:00:00.000Z`,
      workRange: 28800,
      total: 28800,
      date,
    };
  }

  private createMockStatsTotal(index: number): StatsTotal {
    const user = this.mockUsers[index % this.mockUsers.length];
    return {
      id: `task-${index}`,
      total: this.seedInt(0, 28799, 'stats-total', index, user.id),
      userId: user.id,
      name: `Task ${index}`,
      project: `project-${Math.floor(index / 3)}`,
      integration: 'jira',
      integrationData: { key: `TASK-${index}` } as any,
      date: DateTime.now().minus({ days: Math.floor(index / 3) }).toISODate(),
      pagingTotalCount: 30,
      projectName: `Project ${Math.floor(index / 3)}`,
    };
  }

  private createMockAverageGroup(index: number): AverageGroup {
    const average = 28800 - index * 1800;
    return {
      currentTagId: getDemoTeamId(index),
      currentTagName: getDemoTeamName(index),
      total: 28800,
      average,
      averagePerUser: average / (index + 3),
      averageRatio: average / 28800,
    };
  }

  private createMockAttendanceIssue(index: number): WorkSchedulesAttendanceIssue {
    return {
      issues: 2 + index,
      shifts: 10 + index * 2,
      currentTagId: getDemoTeamId(index),
      ratio: 0.05 + index * 0.04,
      currentTagName: getDemoTeamName(index),
    };
  }

  private createMockHoursTrackedUserResult(index: number, options?: NewStatsOptions) {
    const user = this.mockUsers[index % this.mockUsers.length];
    return {
      ...this.createMockModeRatio(index, undefined, options),
      userName: user.name,
      email: user.email,
      userArchived: user.archived,
      unpaidBreak: this.seedInt(0, 899, 'hours-tracked', index, user.id, 'unpaidBreak'),
      paidLeave: 0,
      ratio: 0.75 + this.seedFloat('newStatsTotal', 'user-ratio', index) * 0.15,
    };
  }

  private getMockComcatCatalogSize(): number {
    return this.mockUsers.length * MOCK_COMCAT_SCORES.length;
  }

  private getComcatIndicesForScore(score: TimeuseScore, mode?: MockComcatMode): number[] {
    const size = this.getMockComcatCatalogSize();
    const indices: number[] = [];
    for (let i = 0; i < size; i++) {
      if (resolveMockComcatScore(i, mode) === score) {
        indices.push(i);
      }
    }
    return indices;
  }

  private getUserScoreBucketTotal(
    user: User,
    score: TimeuseScore,
    options?: NewStatsOptions,
    mode?: MockComcatMode,
  ): number {
    const userMode: HighestRatioMode | undefined =
      mode === 'productive' || mode === 'unproductive' || mode === 'neutral' ? mode : undefined;
    const result = this.createMockUserResult(this.mockUserIndex(user), userMode, options);
    return result.score?.find(item => Number(item.id) === score)?.total ?? 0;
  }

  /** Time this user spent on a catalog app; partitions each score bucket so tiles and app rows match. */
  private getUserComcatTime(
    user: User,
    comcatIndex: number,
    options?: NewStatsOptions,
    mode?: MockComcatMode,
  ): number {
    const score = resolveMockComcatScore(comcatIndex, mode);
    const indices = this.getComcatIndicesForScore(score, mode);
    const slot = indices.indexOf(comcatIndex);
    if (slot < 0) {
      return 0;
    }

    const bucket = this.getUserScoreBucketTotal(user, score, options, mode);
    if (bucket <= 0 || !indices.length) {
      return 0;
    }

    const weights = indices.map(index => this.seedFloat('comcat-user-weight', index, user.id) + 0.01);
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
    let allocated = 0;
    for (let i = 0; i < indices.length; i++) {
      const isLast = i === indices.length - 1;
      const value = isLast
        ? Math.max(0, bucket - allocated)
        : Math.floor(bucket * (weights[i] / weightSum));
      if (i === slot) {
        return value;
      }
      allocated += value;
    }
    return 0;
  }

  private createMockUserResult(index: number, mode?: HighestRatioMode, options?: NewStatsOptions) {
    const user = this.mockUsers[index % this.mockUsers.length];
    const workdays = options?.from && options?.to ? this.calculateWorkdays(options) : 1;

    // Initialize the total for this user if it doesn't exist
    if (!this.highRatioModeTotal[user.id]) {
      this.highRatioModeTotal[user.id] = 28800 + this.seedInt(0, 3599, 'user-result-total', user.id);
    }

    if (!this.userUnproductiveRatio[user.id]) {
      this.userUnproductiveRatio[user.id] = this.seedFloat('user-unproductive-ratio', user.id) * 0.35;
    }

    const total = this.highRatioModeTotal[user.id] * workdays;
    const variation = 0.92 + this.seedFloat('user-result-scores', user.id) * 0.16;

    let prodRatio = this.standardRatios.productive * variation;
    let unprodRatio = this.userUnproductiveRatio[user.id];
    let neutralRatio = this.standardRatios.neutral * 0.12 * variation;
    let unratedRatio = Math.max(0.02, 1 - prodRatio - unprodRatio - neutralRatio);
    const ratioSum = prodRatio + unprodRatio + neutralRatio + unratedRatio;
    prodRatio /= ratioSum;
    unprodRatio /= ratioSum;
    neutralRatio /= ratioSum;
    unratedRatio /= ratioSum;

    const prodTotal = Math.floor(total * prodRatio);
    const unprodTotal = Math.floor(total * unprodRatio);
    const neutralTotal = Math.floor(total * neutralRatio);
    const unratedTotal = Math.max(0, total - prodTotal - unprodTotal - neutralTotal);

    const score = [
      { id: String(TimeuseScore.Productive), total: prodTotal, ratio: prodTotal / total },
      { id: String(TimeuseScore.Unproductive), total: unprodTotal, ratio: unprodTotal / total },
      { id: String(TimeuseScore.Neutral), total: neutralTotal, ratio: neutralTotal / total },
      { id: String(TimeuseScore.Unrated), total: unratedTotal, ratio: unratedTotal / total },
    ];

    // Set ratio from mode or ratio-id so preset/highest-ratio sorts use the selected score.
    const ratioId = Number(options?.['ratio-id']);
    let ratio = prodRatio;
    let modTotal = prodTotal;
    if (mode === 'unproductive' || ratioId === TimeuseScore.Unproductive) {
      ratio = unprodRatio;
      modTotal = unprodTotal;
    } else if (mode === 'neutral' || ratioId === TimeuseScore.Neutral) {
      ratio = neutralRatio;
      modTotal = neutralTotal;
    } else if (ratioId === TimeuseScore.Unrated) {
      ratio = unratedRatio;
      modTotal = unratedTotal;
    } else if (mode === 'productive' || ratioId === TimeuseScore.Productive) {
      ratio = prodRatio;
      modTotal = prodTotal;
    }

    return {
      ratioCount: this.seedInt(0, this.mockUsers.length - 1, 'user-result', user.id, 'ratioCount'),
      userId: user.id,
      total,
      ratio,
      ratioTotal: modTotal,
      score,
      email: user.email,
      name: user.name,
      userName: user.name,
      userArchived: user.archived,
      currentTagId: user.tagId,
      currentTagName: user.tagName,
    };
  }

  private createMockComcatResult(
    index: number,
    mode?: MockComcatMode,
    options?: NewStatsOptions,
  ): NewStatsComcatResult {
    const user = this.mockUsers[Math.floor(index / 3) % this.mockUsers.length];
    const users = this.resolveGetTotalUsers(options);
    const total = users.reduce(
      (sum, catalogUser) => sum + this.getUserComcatTime(catalogUser, index, options, mode),
      0,
    );

    // Arrays of realistic application and domain names, categorized by productivity
    const productiveApps = [
      'Microsoft Word', 'Microsoft Excel', 'Microsoft PowerPoint', 'Google Docs',
      'Google Sheets', 'Slack', 'Microsoft Teams', 'Zoom', 'Visual Studio Code',
      'IntelliJ IDEA', 'Adobe Photoshop', 'Adobe Illustrator', 'Figma', 'Notion',
      'Jira', 'Trello', 'Asana', 'GitHub Desktop', 'GitLab', 'Postman',
    ];

    const unproductiveApps = [
      'Netflix', 'YouTube', 'Spotify', 'Steam', 'Epic Games Launcher',
      'Discord', 'TikTok', 'Instagram', 'Facebook', 'Twitter',
      'Twitch', 'Reddit', 'WhatsApp', 'Telegram', 'Snapchat',
    ];

    const neutralApps = [
      'Chrome', 'Firefox', 'Safari', 'Edge', 'Opera',
      'File Explorer', 'Finder', 'Calculator', 'Calendar', 'Mail',
      'Notes', 'Weather', 'Maps', 'Photos', 'Clock',
    ];

    const productiveDomains = [
      'github.com', 'gitlab.com', 'atlassian.com', 'slack.com', 'microsoft.com',
      'office.com', 'google.com', 'docs.google.com', 'drive.google.com', 'zoom.us',
      'figma.com', 'notion.so', 'trello.com', 'asana.com', 'monday.com',
      'airtable.com', 'miro.com', 'linear.app', 'clickup.com', 'hubspot.com',
    ];

    const unproductiveDomains = [
      'netflix.com', 'youtube.com', 'facebook.com', 'instagram.com', 'twitter.com',
      'reddit.com', 'twitch.tv', 'tiktok.com', 'pinterest.com', 'buzzfeed.com',
      'espn.com', 'cnn.com', 'nytimes.com', 'amazon.com', 'ebay.com',
      'etsy.com', 'imgur.com', 'tumblr.com', 'snapchat.com', 'discord.com',
    ];

    const neutralDomains = [
      'wikipedia.org', 'weather.com', 'maps.google.com', 'calendar.google.com',
      'mail.google.com', 'outlook.com', 'apple.com', 'microsoft.com/store',
      'news.google.com', 'translate.google.com', 'dictionary.com',
      'thesaurus.com', 'calculator.net', 'timeanddate.com', 'wolframalpha.com',
    ];

    const score = resolveMockComcatScore(index, mode);
    const isApp = index % 2 === 0;
    let appList = productiveApps;
    let domainList = productiveDomains;

    if (score === TimeuseScore.Unproductive) {
      appList = unproductiveApps;
      domainList = unproductiveDomains;
    } else if (score === TimeuseScore.Neutral || score === TimeuseScore.Unrated) {
      appList = neutralApps;
      domainList = neutralDomains;
    }

    const name = isApp ? appList[index % appList.length] : domainList[index % domainList.length];

    return {
      comCat: `category-${index}`,
      entity: isApp ? 'app' : 'domain',
      name,
      score,
      total,
      userCat: [`user-cat-${index}`],
      userId: user.id,
      count: 1,
    };
  }

  private createMockCompanyResult(_index: number, mode?: HighestRatioMode, options?: NewStatsOptions): NewStatsCompanyResult {
    const users = this.resolveGetTotalUsers(options);
    const userResults = users.map(user => this.createMockUserResult(this.mockUserIndex(user), mode, options));
    const total = userResults.reduce((sum, row) => sum + (row.total || 0), 0);
    const scoreIds = [
      TimeuseScore.Productive,
      TimeuseScore.Unproductive,
      TimeuseScore.Neutral,
      TimeuseScore.Unrated,
    ];
    const score = scoreIds.map((id) => {
      const scoreTotal = userResults.reduce((sum, row) => {
        const item = row.score?.find(entry => Number(entry.id) === id);
        return sum + (item?.total || 0);
      }, 0);
      return {
        id: String(id),
        total: scoreTotal,
        ratio: total ? scoreTotal / total : 0,
      };
    });
    const prod = score.find(item => item.id === String(TimeuseScore.Productive));

    return {
      ratioCount: users.length,
      score,
      total,
      ratio: prod?.ratio ?? 0,
      count: users.length,
    };
  }

  private createMockWorkLifeBalanceResult(index: number): WorkLifeBalanceResult {
    const user = this.mockUsers[index % this.mockUsers.length];
    const seed = index * 19 + (user.id?.length ?? 0);
    const avg = 3.5 + (seed % 12) * 0.65 + (index % 4) * 0.25;
    const roundedAvg = Math.round(avg * 10) / 10;
    const totalIssues = Math.max(0, Math.floor(roundedAvg * 1.1 + (seed % 5)));
    return {
      userId: user.id,
      totalHours: 32 + (seed % 20),
      lastHour: 17 + (seed % 5),
      day: [8, 9, 8, 10, 7, 0, 0],
      tooManyHours: seed % 4,
      lateHours: Math.floor(seed / 4) % 5,
      outOfShiftWork: seed % 3,
      weekendWork: seed % 2,
      dailyIssues: Math.min(totalIssues, Math.max(0, Math.floor(totalIssues / 2))),
      totalIssues,
      total: Math.round(roundedAvg * 7 + totalIssues),
      userName: user.name,
      currentTagId: user.tagId,
      currentTagName: user.tagName,
      average: roundedAvg,
      size: 5,
    };
  }

  public getDemoTotals(apiParams?: any, userCount?: number): Totals {
    // Calculate workdays if date parameters are provided
    const workdays = this.calculateWorkdays(apiParams);

    // Ensure userBaseSeconds is initialized with the correct number of users
    if (this.userBaseSeconds.length !== this.mockUsers.length) {
      this.initUserBaseSeconds();
    }

    const mockUserCount = this.mockUsers.length;
    const specificUserIds = resolveMockUserIds(apiParams);
    const specificBaseSeconds = specificUserIds?.map(id => this.getUserBaseSecondsForTotals(id)) ?? [];
    const useSpecificUsers = specificUserIds?.length > 0 && specificBaseSeconds.every(s => s != null);
    // Group filter is `{ user: 'all-on-reports', tag }`. `resolveMockUserIds` treats that as
    // all users, so totals must resolve tagged users explicitly.
    const taggedUsers = !specificUserIds && this.hasApiTagFilter(apiParams)
      ? this.resolveGetTotalUsers(apiParams)
      : null;

    let resolvedUserCount = userCount ?? resolveMockUserCount(apiParams, mockUserCount);
    let aggregateTotal: number;

    if (taggedUsers) {
      resolvedUserCount = userCount ?? taggedUsers.length;
      aggregateTotal = Math.round(
        taggedUsers.reduce((sum, user) => {
          const baseSeconds = this.getUserBaseSecondsForTotals(user.id) ?? this.baseSeconds;
          return sum + baseSeconds * workdays;
        }, 0),
      );
    } else if (useSpecificUsers) {
      resolvedUserCount = userCount ?? specificUserIds.length;
      aggregateTotal = Math.round(
        specificBaseSeconds.reduce((sum, baseSeconds) => sum + baseSeconds * workdays, 0),
      );
    } else {
      const scaleFactor = mockUserCount > 0 ? resolvedUserCount / mockUserCount : 1;
      const userTotalSeconds = this.userBaseSeconds.map(baseSeconds => parseInt(baseSeconds, 10) * workdays);
      const fullAggregateTotal = userTotalSeconds.reduce((sum, seconds) => sum + seconds, 0);
      aggregateTotal = Math.round(fullAggregateTotal * scaleFactor);
    }

    // Calculate metrics based on standard ratios
    const standardRatios = this.standardRatios;
    const prodSeconds = Math.round(aggregateTotal * standardRatios.productive);
    const unprodSeconds = Math.round(aggregateTotal * standardRatios.unproductive);
    const meetingSeconds = Math.round(aggregateTotal * standardRatios.meeting);
    const idleSeconds = Math.round(aggregateTotal * standardRatios.idle * 60); // Convert to seconds
    const manualSeconds = Math.round(aggregateTotal * standardRatios.manual);
    const mobileSeconds = Math.round(aggregateTotal * standardRatios.mobile);
    const paidBreakSeconds = Math.round(aggregateTotal * standardRatios.paidBreak);
    const neutralSeconds = Math.round(aggregateTotal * standardRatios.neutral);
    const unratedSeconds = Math.round(aggregateTotal * 0.05); // 5% unrated

    const countScale = mockUserCount > 0 ? resolvedUserCount / mockUserCount : 1;
    const scaledNewUsers = Math.round(Math.max(1, workdays / 30) * countScale);
    const scaledPaidLeave = Math.round(Math.max(1, workdays / 20) * countScale);

    return {
      total: aggregateTotal,
      meeting: meetingSeconds,
      idleMins: Math.round(idleSeconds / 60),
      unprod: unprodSeconds,
      prod: prodSeconds,
      manual: manualSeconds,
      mobile: mobileSeconds,
      paidBreak: paidBreakSeconds,
      'unrated&neutral': neutralSeconds + unratedSeconds,
      unrated: unratedSeconds,
      meetingRatio: standardRatios.meeting,
      idleMinsRatio: standardRatios.idle,
      unprodRatio: standardRatios.unproductive,
      prodRatio: standardRatios.productive,
      manualRatio: standardRatios.manual,
      mobileRatio: standardRatios.mobile,
      paidBreakRatio: standardRatios.paidBreak,
      count: resolvedUserCount,
      newUsersTotal: resolvedUserCount > 1 ? Math.max(1, scaledNewUsers) : 0,
      paidLeave: Math.max(1, scaledPaidLeave),
      idleSecRatio: standardRatios.idle,
      idleSec: idleSeconds,
      keysPerNonIdleMin: 120,
      test: 0,
    };
  }

  private hasApiTagFilter(apiParams?: { tag?: string | string[] }): boolean {
    if (apiParams?.tag == null || apiParams.tag === '') {
      return false;
    }
    const tags = Array.isArray(apiParams.tag) ? apiParams.tag : String(apiParams.tag).split(',');
    return tags.some(tag => !!`${tag}`.trim());
  }

  /** Per-user daily base seconds for mock totals; null when the id is not a known demo user. */
  private getUserBaseSecondsForTotals(userId: string): number | null {
    if (!this.mockDataStore.getUser(userId)) {
      return null;
    }
    return this.mockDataStore.getBaseTime(userId);
  }

  private calculateWorkdays(apiParams?: any): number {
    if (!apiParams?.from || !apiParams?.to) {
      return 1;
    }

    const fromDate = DateTime.fromISO(apiParams.from, { zone: 'utc' }).startOf('day');
    const toDate = DateTime.fromISO(apiParams.to, { zone: 'utc' }).startOf('day');

    let workdays = 0;
    let currentDate = fromDate;

    while (currentDate <= toDate) {
      if (currentDate.weekday <= 5) {
        workdays++;
      }
      currentDate = currentDate.plus({ days: 1 });
    }

    return Math.max(1, workdays);
  }

  ngOnDestroy(): void {
    // Clear memory cleanup timeout to prevent memory leaks
    if (this.memoryCleanupTimeoutId) {
      clearTimeout(this.memoryCleanupTimeoutId);
      this.memoryCleanupTimeoutId = null;
    }

    // Cleanup when service is destroyed
    this.cleanup();

    // Cleanup MockDataStore instance
    MockDataStore.cleanup();
  }

  // Cleanup method to prevent memory leaks
  private cleanup(): void {
    // Clear any cached data
    this.userBaseSeconds = [];
    this.baseSeconds = 0;

    // Clear any stored ratios
    Object.keys(this.highRatioModeTotal).forEach(key => {
      delete this.highRatioModeTotal[key];
    });

    Object.keys(this.userUnproductiveRatio).forEach(key => {
      delete this.userUnproductiveRatio[key];
    });

    // Clear object pools
    this.objectPool.clear();
  }

  /**
   * Performs periodic memory cleanup to prevent accumulation
   */
  private performMemoryCleanup(): void {
    // Clear object pools every 10 minutes to prevent memory leaks
    this.memoryCleanupTimeoutId = setTimeout(() => {
      this.objectPool.clear();
      this.performMemoryCleanup(); // Schedule next cleanup
    }, 10 * 60 * 1000);
  }

  // Helper method to get object from pool or create new one
  private getFromPool<T>(poolKey: string, factory: () => T): T {
    if (!this.objectPool.has(poolKey)) {
      this.objectPool.set(poolKey, []);
    }

    const pool = this.objectPool.get(poolKey);
    return pool.length > 0 ? pool.pop() : factory();
  }

  // Helper method to return object to pool
  private returnToPool<T>(poolKey: string, obj: T): void {
    if (!this.objectPool.has(poolKey)) {
      this.objectPool.set(poolKey, []);
    }

    const pool = this.objectPool.get(poolKey);
    if (pool.length < this.poolSize) {
      pool.push(obj);
    }
  }
}
